import { CreditCard, Plus, Star, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import axiosInstance from '../../helpers/axiosConfig';
import { authHeaders, parseApiError } from '../../helpers/billingApi';
import { confirmAndSaveCard } from '../../helpers/paymentMethodsApi';
import { getStripe } from '../../helpers/stripeClient';
import CardField from './CardField';

// The card-collection form, rendered inside <Elements>. The SetupIntent ->
// confirmCardSetup -> persist sequence lives in paymentMethodsApi so this and
// the subscribe modal collect cards identically.
function AddCardForm({ onSaved, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [makeDefault, setMakeDefault] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cardError, setCardError] = useState(''); // inline, card-level
  const [apiError, setApiError] = useState(''); // banner, API-level

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || saving) return;
    setSaving(true);
    setCardError('');
    setApiError('');

    try {
      const { cardError: rejected } = await confirmAndSaveCard({
        stripe,
        elements,
        makeDefault,
      });
      if (rejected) {
        // Card-level problem (declined, invalid) — fix it in the form.
        setCardError(rejected);
        setSaving(false);
        return;
      }

      onSaved();
    } catch (requestError) {
      setApiError(parseApiError(requestError, 'Could not save your card.'));
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-body">
        <h2>Add a payment method</h2>
        <p>Your card is stored securely with Stripe for future billing. You will not be charged now.</p>
        {apiError ? <div className="error-banner">{apiError}</div> : null}
        <CardField error={cardError} onChange={() => setCardError('')} />
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={makeDefault}
            onChange={(event) => setMakeDefault(event.target.checked)}
          />
          <span>Set as default payment method</span>
        </label>
      </div>
      <footer className="modal-foot">
        <span>NO CHARGE — STORED FOR FUTURE BILLING</span>
        <div>
          <button className="btn btn-ghost" type="button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" type="submit" disabled={!stripe || saving}>
            {saving ? 'Saving...' : 'Save card'}
          </button>
        </div>
      </footer>
    </form>
  );
}

function AddCardModal({ open, onSaved, onClose }) {
  if (!open) return null;
  const stripePromise = getStripe();

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="create-modal" role="dialog" aria-modal="true" aria-labelledby="add-card-title">
        <header className="modal-head">
          <div className="modal-head-copy">
            <div className="modal-kicker">
              <span>PAYMENT METHOD</span>
              Add a card
            </div>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close add card">
            <X size={16} />
          </button>
        </header>
        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <AddCardForm onSaved={onSaved} onClose={onClose} />
          </Elements>
        ) : (
          <div className="modal-body">
            <div className="error-banner">
              Card payments are not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY and reload.
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default function PaymentMethods() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const loadMethods = useCallback(async () => {
    setError('');
    try {
      const { data } = await axiosInstance.get('payment_methods/', authHeaders());
      setMethods(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setError(parseApiError(requestError, 'Could not load your saved cards.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMethods();
  }, [loadMethods]);

  const setDefault = async (id) => {
    setBusyId(id);
    setError('');
    try {
      await axiosInstance.put(`payment_methods/${id}`, { is_default: true }, authHeaders());
      await loadMethods();
    } catch (requestError) {
      setError(parseApiError(requestError, 'Could not update your default card.'));
    } finally {
      setBusyId(null);
    }
  };

  const deleteMethod = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove this card from your account?')) return;
    setBusyId(id);
    setError('');
    try {
      await axiosInstance.delete(`payment_methods/${id}`, authHeaders());
      await loadMethods();
    } catch (requestError) {
      setError(parseApiError(requestError, 'Could not remove this card.'));
    } finally {
      setBusyId(null);
    }
  };

  const onSaved = async () => {
    setModalOpen(false);
    setLoading(true);
    await loadMethods();
  };

  return (
    <section className="panel-card card-pad payment-card">
      <div className="payment-card-head">
        <div className="payment-card-title">
          <CreditCard size={20} />
          <h2>Payment methods</h2>
        </div>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      {loading ? (
        <p>Loading cards...</p>
      ) : methods.length ? (
        <>
          <ul className="card-list">
            {methods.map((method) => (
              <li className="card-row" key={method.id}>
                <div className="card-row-main">
                  <strong>
                    {(method.brand || 'Card').toUpperCase()} •••• {method.last_four}
                  </strong>
                  <small>
                    Expires {method.exp_month}/{method.exp_year}
                    {method.is_default ? <span className="default-badge">Default</span> : null}
                  </small>
                </div>
                <div className="card-row-actions">
                  {!method.is_default ? (
                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      onClick={() => setDefault(method.id)}
                      disabled={busyId === method.id}
                    >
                      <Star size={14} />
                      Set default
                    </button>
                  ) : null}
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    onClick={() => deleteMethod(method.id)}
                    disabled={busyId === method.id}
                    aria-label="Remove card"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <button className="btn btn-ghost btn-sm" type="button" onClick={() => setModalOpen(true)}>
            <Plus size={15} />
            Add card
          </button>
        </>
      ) : (
        <>
          <strong>No card on file</strong>
          <p>Add a payment method before publishing a staging site.</p>
          <button className="btn btn-primary btn-sm" type="button" onClick={() => setModalOpen(true)}>
            <Plus size={15} />
            Add card
          </button>
        </>
      )}

      <AddCardModal open={modalOpen} onSaved={onSaved} onClose={() => setModalOpen(false)} />
    </section>
  );
}
